// @flow
import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {IconButton, makeStyles, Typography} from '@material-ui/core';

import {colors} from '../../../../../assets/styles/colors';
import {CancelOutlined, PublishRounded} from '@material-ui/icons';
import CropModal from '../../../../../elements/CropModal';
import type {ImageToCrop} from '../../../../../elements/CropModal';

type Props = {
  disabled?: boolean,
  placeholder: string,
  image?: string | null,
  aspect?: number,
  previewStyle?: string,
  maxHeight?: number,
  maxWidth?: number,
  recommendedWidth?: number,
  recommendedHeight?: number,
  setImage: (val: string | null) => void,
  setImageFile: (val: File | null) => void,
  keepFormat?: boolean,
  hideRecommendSize?: boolean,
  id?: string,
};

const useStyles = makeStyles(theme => ({
  imageBoxDashed: {
    height: '100%',
    maxHeight: '100%',
    width: '100%',
    border: `2px dashed ${colors.grey500}`,
    backgroundColor: '#FFFFFF',
    boxSizing: 'border-box',
    borderRadius: '12px',
    padding: theme.spacing(3, 2),
    textAlign: 'center',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    color: colors.purple3,
    '&:focus-within': {
      border: `2px dashed ${theme.palette.primary.main}`,
    },
  },
  defaultCursor: {
    cursor: 'unset',
  },
  imageBox: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #E8E8E9',
    boxSizing: 'border-box',
    borderRadius: theme.borderRadius,
    padding: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    maxHeight: '100%',
    width: '100%',
  },
  removeImageIcon: {
    position: 'absolute',
    top: 4,
    right: 4,
  },
  image: {
    display: 'block',
    margin: 'auto',
    height: '100%',
    width: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'cover',
  },
  imageUploadTip: {
    marginTop: theme.spacing(1),
    fontSize: '14px',
  },
  button: {
    height: '100%',
    width: '100%',
    padding: 0,
    '& span': {
      height: '100%',
    },
  },
}));

export function ImageButton(props: Props) {
  const {
    disabled = false,
    image,
    setImage,
    setImageFile,
    placeholder,
    aspect = 1,
    previewStyle,
    keepFormat,
    hideRecommendSize,
    id,
  } = props;

  const classes = useStyles();

  const [imageToCrop, setImageToCrop] = useState<ImageToCrop | null>(null);

  const handleFileInputChange = async (
    event: SyntheticInputEvent<HTMLInputElement>
  ) => {
    const file = event.target?.files[0];
    if (!file) return;

    setImageToCrop({
      preview: URL.createObjectURL(file),
      file,
    });
  };

  const handleRemoveFileImage = () => {
    setImage(null);
    setImageFile(null);
  };

  const recommendedWidth = props.recommendedWidth || 600;
  const recommendedHeight =
    props.recommendedHeight || (recommendedWidth / aspect).toFixed(0);

  let imageField;
  if (!!image) {
    const imageContainerStyle = {
      aspectRatio: aspect.toString(),
    };

    imageField = (
      <div
        className={classes.imageBox}
        data-aspect={aspect}
        style={imageContainerStyle}
      >
        <img
          className={`${classes.image} ${previewStyle ? previewStyle : ''}`}
          src={image}
          alt=""
        />
        <IconButton
          aria-label={'cancel'}
          className={classes.removeImageIcon}
          onClick={handleRemoveFileImage}
        >
          <CancelOutlined />
        </IconButton>
      </div>
    );
  } else {
    imageField = (
      <label className={classes.imageBoxDashed} aria-label={placeholder}>
        <PublishRounded color={true ? 'primary' : 'disabled'} />
        <Typography className={classes.imageUploadTip}>
          {placeholder}
        </Typography>
        {!hideRecommendSize && (
          <Typography className={classes.imageUploadTip}>
            {`${recommendedWidth} x ${recommendedHeight} px`}
          </Typography>
        )}
        <input
          hidden
          type="file"
          id={id}
          accept="image/*"
          disabled={disabled}
          onChange={handleFileInputChange}
        />
      </label>
    );
  }

  return (
    <>
      {imageField}

      {imageToCrop && (
        <CropModal
          open
          aspect={aspect}
          onClose={() => setImageToCrop(null)}
          images={[imageToCrop]}
          onSubmit={result => {
            const [imageCropped] = result;
            setImage(imageCropped.preview);
            setImageFile(imageCropped.file);
            setImageToCrop(null);
          }}
          compressOptions={{
            quality: 1, // threshold: 1mb
            threshold: 921600,
            maxWidth: 1000,
            maxHeight: 1000 / aspect,
          }}
          keepFormat={keepFormat}
        />
      )}
    </>
  );
}

ImageButton.propTypes = {
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  image: PropTypes.string,
  aspect: PropTypes.number,
  previewStyle: PropTypes.string,
  maxHeight: PropTypes.number,
  maxWidth: PropTypes.number,
  setImage: PropTypes.func.isRequired,
  setImageFile: PropTypes.func.isRequired,
};
